/**
 * sidebar.store — estado de colapso da navegação lateral.
 *
 * Dois níveis de estado:
 *   • `preferCollapsed` — preferência MANUAL do usuário, persistida em
 *     localStorage. É o baseline usado em rotas normais.
 *   • `collapsed` — estado EFETIVO (efêmero) refletido na UI. Em rotas de
 *     auto-colapso (ver `sidebarRoutes.ts`) fica `true` independente da
 *     preferência; nas demais, segue `preferCollapsed`.
 *
 * Fluxo:
 *   - `toggle()` / `setCollapsed()` → ação manual do mini-botão: atualiza o
 *     efetivo E o baseline persistido (a escolha explícita mais recente vale
 *     como preferência global).
 *   - `applyRoute(isAuto)` → chamado a cada troca de rota pelo AppShell:
 *     colapsa se a rota é de auto-colapso, senão restaura a preferência.
 *
 * Segue o padrão de `theme.store` (persistência try/catch, sem middleware).
 */
import { create } from 'zustand'

import { STORAGE_KEYS } from '@/lib/constants'

export interface SidebarState {
  /** Estado efetivo exibido na UI. */
  collapsed: boolean
  /** Preferência manual persistida (baseline de rotas normais). */
  preferCollapsed: boolean
}

export interface SidebarActions {
  /** Alterna o estado efetivo e persiste como preferência. */
  toggle: () => void
  /** Define o estado efetivo e persiste como preferência. */
  setCollapsed: (collapsed: boolean) => void
  /**
   * Aplica a regra de rota: `true` força colapso; `false` restaura a
   * preferência manual. Não persiste (colapso por rota é transitório).
   */
  applyRoute: (isAutoCollapse: boolean) => void
}

export type SidebarStore = SidebarState & SidebarActions

function readPersisted(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.SIDEBAR) === 'collapsed'
  } catch {
    return false
  }
}

function writePersisted(collapsed: boolean): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEYS.SIDEBAR,
      collapsed ? 'collapsed' : 'expanded',
    )
  } catch {
    // localStorage indisponível — ignora silenciosamente.
  }
}

const initialPreferred = readPersisted()

export const useSidebarStore = create<SidebarStore>((set, get) => ({
  collapsed: initialPreferred,
  preferCollapsed: initialPreferred,

  toggle: () => {
    const next = !get().collapsed
    writePersisted(next)
    set({ collapsed: next, preferCollapsed: next })
  },

  setCollapsed: (collapsed) => {
    writePersisted(collapsed)
    set({ collapsed, preferCollapsed: collapsed })
  },

  applyRoute: (isAutoCollapse) => {
    set((s) => ({ collapsed: isAutoCollapse ? true : s.preferCollapsed }))
  },
}))
